import React, { useMemo } from 'react'
import type { PaymentTransaction } from '@/types/payment'
import { getPaymentChannelInfo } from '@/types/payment'

interface PaymentInstructionsProps {
  payment: PaymentTransaction
  onCopyCode?: () => void
}

export const PaymentInstructions: React.FC<PaymentInstructionsProps> = ({
  payment,
  onCopyCode,
}) => {
  const channelInfo = useMemo(
    () => getPaymentChannelInfo(payment.paymentChannel),
    [payment.paymentChannel]
  )

  const expiryDate = useMemo(() => {
    if (!payment.expiryTime) return null
    return new Date(payment.expiryTime)
  }, [payment.expiryTime])

  const handleCopyCode = () => {
    if (payment.paymentCode) {
      navigator.clipboard.writeText(payment.paymentCode)
      onCopyCode?.()
    }
  }

  const handleOpenPaymentUrl = () => {
    if (payment.paymentUrl) {
      window.open(payment.paymentUrl, '_blank')
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount)
  }

  const formatDateTime = (date: Date) => {
    return new Intl.DateTimeFormat('id-ID', {
      dateStyle: 'long',
      timeStyle: 'short',
    }).format(date)
  }

  return (
    <div className="space-y-6">
      {/* Payment Method Header */}
      <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
        <span className="text-3xl">{channelInfo?.icon || '💳'}</span>
        <div>
          <h3 className="font-semibold text-gray-900">{channelInfo?.name || payment.paymentChannel}</h3>
          <p className="text-sm text-gray-600">{channelInfo?.description}</p>
        </div>
      </div>

      {/* Amount Summary */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Subtotal</span>
          <span>{formatCurrency(payment.amount)}</span>
        </div>
        {payment.discount > 0 && (
          <div className="flex justify-between text-sm text-green-600">
            <span>Discount</span>
            <span>-{formatCurrency(payment.discount)}</span>
          </div>
        )}
        {payment.fee > 0 && (
          <div className="flex justify-between text-sm text-gray-600">
            <span>Payment Fee</span>
            <span>{formatCurrency(payment.fee)}</span>
          </div>
        )}
        <hr className="my-2" />
        <div className="flex justify-between font-semibold text-lg">
          <span>Total</span>
          <span className="text-blue-600">{formatCurrency(payment.netAmount + payment.fee)}</span>
        </div>
      </div>

      {/* Payment Instructions based on method */}
      {payment.paymentMethod === 'virtual_account' && payment.paymentCode && (
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Virtual Account Number</h4>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-white px-4 py-3 rounded border text-xl font-mono tracking-wider">
                {payment.paymentCode}
              </code>
              <button
                onClick={handleCopyCode}
                className="px-4 py-3 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                Copy
              </button>
            </div>
          </div>

          <div className="text-sm text-gray-600 space-y-2">
            <h4 className="font-medium text-gray-900">Payment Instructions:</h4>
            <ol className="list-decimal list-inside space-y-1">
              <li>Open your mobile banking or ATM</li>
              <li>Select Transfer to Virtual Account</li>
              <li>Enter the Virtual Account number above</li>
              <li>Verify the amount and recipient name</li>
              <li>Complete the payment</li>
            </ol>
          </div>
        </div>
      )}

      {payment.paymentMethod === 'qris' && payment.paymentCode && (
        <div className="space-y-4">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
            <h4 className="font-medium text-gray-900 mb-3">Scan QR Code</h4>
            {/* In a real implementation, this would be a QR code image */}
            <div className="inline-block bg-white p-4 rounded-lg border">
              <div className="w-48 h-48 bg-gray-200 flex items-center justify-center rounded">
                <span className="text-gray-500 text-sm text-center">
                  QR Code<br />
                  (Use payment URL below)
                </span>
              </div>
            </div>
          </div>

          {payment.paymentUrl && (
            <button
              onClick={handleOpenPaymentUrl}
              className="w-full py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Open Payment Page
            </button>
          )}

          <div className="text-sm text-gray-600 space-y-2">
            <h4 className="font-medium text-gray-900">Payment Instructions:</h4>
            <ol className="list-decimal list-inside space-y-1">
              <li>Open any e-wallet app that supports QRIS</li>
              <li>Scan the QR code above</li>
              <li>Verify the amount and merchant name</li>
              <li>Complete the payment</li>
            </ol>
          </div>
        </div>
      )}

      {payment.paymentMethod === 'ewallet' && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">E-Wallet Payment</h4>
            <p className="text-sm text-gray-600 mb-4">
              Click the button below to open {channelInfo?.name} and complete your payment.
            </p>
            {payment.paymentUrl && (
              <button
                onClick={handleOpenPaymentUrl}
                className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Pay with {channelInfo?.name}
              </button>
            )}
          </div>

          <div className="text-sm text-gray-600 space-y-2">
            <h4 className="font-medium text-gray-900">Payment Instructions:</h4>
            <ol className="list-decimal list-inside space-y-1">
              <li>Click the button above to open the e-wallet app</li>
              <li>Verify the payment amount</li>
              <li>Enter your PIN or authenticate</li>
              <li>Payment will be confirmed automatically</li>
            </ol>
          </div>
        </div>
      )}

      {payment.paymentMethod === 'card' && payment.paymentUrl && (
        <div className="space-y-4">
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">Credit/Debit Card</h4>
            <p className="text-sm text-gray-600 mb-4">
              Click the button below to securely enter your card details.
            </p>
            <button
              onClick={handleOpenPaymentUrl}
              className="w-full py-3 bg-indigo-600 text-gray-900 dark:text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Enter Card Details
            </button>
          </div>

          <div className="text-sm text-gray-600 space-y-2">
            <h4 className="font-medium text-gray-900">Secure Payment:</h4>
            <ul className="list-disc list-inside space-y-1">
              <li>Your card information is encrypted</li>
              <li>We support Visa, Mastercard, and JCB</li>
              <li>3D Secure authentication may be required</li>
            </ul>
          </div>
        </div>
      )}

      {/* Expiry Warning */}
      {expiryDate && payment.status === 'pending' && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <span className="text-orange-500">⚠️</span>
            <div>
              <h4 className="font-medium text-orange-800">Payment Deadline</h4>
              <p className="text-sm text-orange-700">
                Complete your payment before{' '}
                <strong>{formatDateTime(expiryDate)}</strong>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PaymentInstructions
